import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getFighterListingPaged } from '../services/CMSService';

const DIVISIONS = [
  { label: 'All Divisions', value: '' },
  { label: 'Heavyweight', value: 'Heavyweight' },
  { label: 'Light Heavyweight', value: 'Light Heavyweight' },
  { label: 'Middleweight', value: 'Middleweight' },
  { label: 'Welterweight', value: 'Welterweight' },
  { label: 'Lightweight', value: 'Lightweight' },
  { label: 'Featherweight', value: 'Featherweight' },
  { label: 'Bantamweight', value: 'Bantamweight' },
  { label: "Women's Strawweight", value: "Women's Strawweight" },
  { label: 'Women Flyweight', value: 'Women Flyweight' },
  { label: 'Women Bantamweight', value: 'Women Bantamweight' },
];

const SORT_OPTIONS = [
  { label: 'A to Z', value: 'az' },
  { label: 'Z to A', value: 'za' },
  { label: 'Heaviest', value: 'heaviest' },
  { label: 'Lightest', value: 'lightest' },
  { label: 'Number of Wins', value: 'wins' },
];

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const FighterCard = ({ fighter }) => {
  return (
    <Link
      to={`/fighters/${fighter.id}`}
      className="group block rounded-lg overflow-hidden bg-secondary transition-all duration-300 ease-out will-change-transform hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(255,212,63,0.32),0_8px_24px_-12px_rgba(255,212,63,0.4),0_0_32px_-14px_rgba(255,212,63,0.3)]"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-dark-gray">
        <img
          src={fighter.img || '/assets/images/placeholder_fighter.png'}
          alt={fighter.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={e => {
            e.target.src = '/assets/images/placeholder_fighter.png';
          }}
        />
        {fighter.champion && (
          <div
            className="absolute top-0 left-0 right-0 px-3 py-1 text-center font-display text-xs tracking-wider text-white uppercase leading-tight"
            style={{
              background: 'linear-gradient(to right, #d4af37, #b8860b)',
              clipPath: 'polygon(0 0, 100% 0, 100% 80%, 50% 100%, 0 80%)',
              paddingBottom: '0.75rem',
            }}
          >
            {fighter.fullFighter?.weight_class
              ? `${fighter.fullFighter.weight_class} Champion`
              : 'Champion'}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-display text-xl text-white truncate">{fighter.title}</h3>
        {fighter.fullFighter?.weight_class && (
          <p className="text-accent text-xs font-display tracking-wider uppercase truncate">
            {fighter.fullFighter.weight_class}
          </p>
        )}
        <p className="text-text-muted text-sm font-body mt-0.5">
          {fighter.wins}W - {fighter.losses}L - {fighter.draws}D
        </p>
      </div>
    </Link>
  );
};

const FightersPage = () => {
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [division, setDivision] = useState('');
  const [sortBy, setSortBy] = useState('wins');
  const [activeLetter, setActiveLetter] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const PAGE_SIZE = 100;

  const fetchFighters = useCallback(
    async (currentOffset, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const results = await getFighterListingPaged(
          currentOffset,
          division,
          activeLetter,
          '',
          sortBy,
        );

        if (append) {
          setFighters(prev => [...prev, ...results]);
        } else {
          setFighters(results);
        }

        setHasMore(results.length >= PAGE_SIZE);
      } catch (error) {
        console.error('Error fetching fighters:', error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [division, activeLetter, sortBy],
  );

  // Reset and fetch when filters change
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchFighters(0, false);
  }, [fetchFighters]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const newOffset = offset + PAGE_SIZE;
          setOffset(newOffset);
          fetchFighters(newOffset, true);
        }
      },
      { threshold: 0.1 },
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, offset, fetchFighters]);

  const handleLetterClick = letter => {
    setActiveLetter(activeLetter === letter ? '' : letter);
  };

  return (
    <div className="min-h-screen bg-primary text-white font-body">
      {/* Gold gradient top section */}
      <div className="bg-gradient-to-b from-[rgba(179,144,21,0.13)] to-[rgba(20,20,19,0.6)]">
        <div className="container mx-auto px-4 pt-8 pb-6">
          <h1 className="font-display text-5xl md:text-6xl tracking-wide text-white mb-6">
            FIGHTERS
          </h1>

          {/* Filters row */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* Division filter */}
            <div className="relative">
              <select
                value={division}
                onChange={e => setDivision(e.target.value)}
                className="appearance-none bg-secondary border border-gray-border rounded-lg px-4 py-2.5 pr-10 text-white font-body text-sm focus:outline-none focus:border-accent cursor-pointer min-w-[200px]"
              >
                {DIVISIONS.map(d => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* Sort by */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="appearance-none bg-secondary border border-gray-border rounded-lg px-4 py-2.5 pr-10 text-white font-body text-sm focus:outline-none focus:border-accent cursor-pointer min-w-[180px]"
              >
                {SORT_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>
                    Sort: {s.label}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Letter selector */}
          <div className="flex flex-wrap gap-1">
            {LETTERS.map(letter => (
              <button
                key={letter}
                onClick={() => handleLetterClick(letter)}
                className={`w-9 h-9 flex items-center justify-center rounded text-sm font-display tracking-wider transition-colors duration-150 ${
                  activeLetter === letter
                    ? 'bg-[#625423] text-accent'
                    : 'text-text-muted hover:text-white hover:bg-white/10'
                }`}
              >
                {letter}
              </button>
            ))}
            {activeLetter && (
              <button
                onClick={() => setActiveLetter('')}
                className="ml-2 px-3 h-9 flex items-center justify-center rounded text-sm font-body text-accent hover:text-accent-hover transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Fighter grid */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fighters.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-text-muted text-lg font-body">
              No fighters found matching your criteria.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-8">
              {fighters.map(fighter => (
                <FighterCard key={fighter.id} fighter={fighter} />
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-10 mt-8">
              {loadingMore && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FightersPage;
